import { isNullOrUndefined } from '~/util/validators/baseComponentsValidators'
import { generateRequestHashHeader } from './helpers'
import useUrl from './helpers/useUrl'
import { collectAndHash } from '~/util/fingerprint'
import { makeHeadersWithRecaptcha } from '@/services/_api/helpers/_constants'
const isStage = process.env.BASE_URL === 'https://ui2.stagecb.sharkscode.com'
/** @param {import("@nuxtjs/axios").NuxtAxiosInstance} client */
export default (client, { $locale }) => {
  const root = useUrl('auth')
  return {
    /** @param {Object} LoginDTO
     * @param {string} LoginDTO.password
     * @param {string} LoginDTO.type
     * @param {string?} [LoginDTO.email=null]
     * @param {string?} [LoginDTO.phone_code=null]
     * @param {string?} [LoginDTO.phone=null]
     *
     * @return {Promise<AxiosResponse<LoginResponse>>} */
    async login({ password, type, email = null, phone_code = null, phone = null }) {
      const url = root('login')
      const hash = isStage ? '' : await collectAndHash()
      const headers =  generateRequestHashHeader(hash)

      return client.post(url, {
        password,
        type,
        ...(!isNullOrUndefined(email) && { email }),
        ...(!isNullOrUndefined(phone_code) && { phone_code }),
        ...(!isNullOrUndefined(phone) && { phone }),
      }, { headers })
    },

    /** @param {Object} RegisterDTO
     * @param {string} RegisterDTO.password
     * @param {string} RegisterDTO.type
     * @param {1 | 0} RegisterDTO.is_accept
     * @param {string} RegisterDTO.language
     * @param {string} RegisterDTO.promokey
     * @param {string?} [RegisterDTO.email=null]
     * @param {string?} [RegisterDTO.phone_code=null]
     * @param {string?} [RegisterDTO.phone=null]
     *
     * @returns {Promise<AxiosResponse<RegisterResponse>>} */
    async register({
      password,
      type,
      is_accept,
      language,
      promokey,
      email = null,
      phone_code = null,
      phone = null,
      recaptchaToken = null,
    }) {
      const url = root('register')
      const hash = isStage ? '' :  await collectAndHash()
      const hashHeader = generateRequestHashHeader(hash)

      /** recaptcha logic */
      const headers = makeHeadersWithRecaptcha(recaptchaToken, hashHeader)

      return client.post(url, {
        password,
        type,
        is_accept,
        language,
        promokey,
        ...(!isNullOrUndefined(email) && { email }),
        ...(!isNullOrUndefined(phone_code) && { phone_code }),
        ...(!isNullOrUndefined(phone) && { phone }),
      }, { headers })
    },

    /** @param {Object} MigrationRegisterDTO
     * @param {string} MigrationRegisterDTO.token
     *
     * @returns {Promise<AxiosResponse<RegisterResponse>>} */
    async migrationRegister({ token, eventId, isBanner }) {
      const url = root('migration/register')
      return client.post(url, { token, eventId, isBanner })
    },

    /** @param {Object} ChangePasswordDTO
     * @param {string} ChangePasswordDTO.old_password
     * @param {string} ChangePasswordDTO.password
     * @param {string} ChangePasswordDTO.password2
     *
     * @returns {Promise<AxiosResponse<ChangePasswordResponse>} */
    async profileChangePassword({ old_password, password, password2 }) {
      const url = root('password/manual-change')
      return client.post(url, { old_password, password, password2 })
    },

    /** @param {Object} SendEmailConfirmDTO
     * @param {string} SendEmailConfirmDTO.email
     * @param {string?} [SendEmailConfirmDTO.recaptchaToken=null]
     *
     * @returns {Promise<AxiosResponse<StatusTrueResponse>>} */
    async profileSendEmailConfirm({ email, recaptchaToken = null }) {
      const url = root('verification/send-email')
      const headers = makeHeadersWithRecaptcha(recaptchaToken)
      return client.post(url, { email }, { headers })
    },

    /**  @param {Object} VerifyEmailDTO
     * @param {string} VerifyEmailDTO.verify_token
     *
     * @returns {Promise<AxiosResponse<StatusTrueResponse>>} */
    async profileVerifyEmail({ verify_token }) {
      const url = root('verification/confirm-email')
      return client.post(url, { verify_token })
    },

    /** @param {Object} PhoneConfirmDTO
     * @param {string} PhoneConfirmDTO.phone
     * @param {string} PhoneConfirmDTO.phone_code
     * @param {string?} [PhoneConfirmDTO.recaptchaToken=null]
     *
     * @returns {Promise<AxiosResponse<SendPhoneConfirmResponse>>} */
    async profileSendPhoneConfirm({ phone, phone_code, recaptchaToken = null }) {
      const url = root('verification/send-sms')
      const headers = makeHeadersWithRecaptcha(recaptchaToken)
      return client.post(url, { phone, phone_code }, { headers })
    },

    /** @param {Object} VerifyPhoneDTO
     * @param {string} VerifyPhoneDTO.code
     *
     * @returns {Promise<AxiosResponse<StatusTrueResponse>>} */
    async profileVerifyPhone({ code, phone }) {
      const url = root('verification/confirm-phone')
      return client.post(url, { code, phone })
    },

    /** @returns {Promise<AxiosResponse<SMSAvailabilityResponse>>} */
    async checkSmsAvailability() {
      const url = root('verification/check-sms-availability')
      return client.post(url)
    },

    /** @param {Object} RestorePasswordFormDTO
     * @param {string} RestorePasswordFormDTO.password
     * @param {string} RestorePasswordFormDTO.password2
     *
     * @returns {Promise<AxiosResponse<StatusTrueResponse>>} */
    async restorePassForm({ password, password2 }) {
      const url = root('password/change')
      return client.post(url, { password, password2 })
    },

    /** @param {Object} RestorePassReqDTO
     * @param {string} RestorePassReqDTO.type
     * @param {string?} [RestorePassReqDTO.email=null]
     * @param {string?} [RestorePassReqDTO.phone_code=null]
     * @param {string?} [RestorePassReqDTO.phone=null]
     *
     * @returns{Promise<AxiosResponse<StatusTrueResponse>>} */
    async restorePassReq({ type, email = null, phone_code = null, phone = null, recaptchaToken = null }) {
      const url = root('password/forgot')
      const headers = makeHeadersWithRecaptcha(recaptchaToken)
      return client.post(url, {
        type,
        ...(!isNullOrUndefined(email) && { email }),
        ...(!isNullOrUndefined(phone_code) && { phone_code }),
        ...(!isNullOrUndefined(phone) && { phone }),
      }, { headers })
    },

    /** @param {Object} RestorePassConfirmDTO
     * @param {string} RestorePassConfirmDTO.restore_token
     * @param {string?} [RestorePassConfirmDTO.recaptchaToken=null]
     *
     * @returns{Promise<AxiosResponse<RestorePassConfirmResponse>>}  */
    async restorePassConfirm({ restore_token, recaptchaToken = null }) {
      const url = root('password/reset')
      const headers = makeHeadersWithRecaptcha(recaptchaToken)
      return client.post(url, { restore_token }, { headers })
    },

    /**
     * @param {Object} UserSocialDTO
     * @param {string} UserSocialDTO.social
     * @param {string} [UserSocialDTO.lang]
     *
     * @returns {Promise<AxiosResponse<UserSocialResponse>>} */
    async userSocial({ social, lang = $locale() }) {
      const url = root('social')
      const params = {
        type: social,
        language: lang,
      }

      return client.get(url, { params })
    },

    /** @param {Object} UserSocialCBDTO
     * @param {string} UserSocialCBDTO.cb
     * @param {1 | 0} [UserSocialCBDTO.is_accept=0]
     *
     * */
    async userSocialCb({ cb, is_accept = 0 }) {
      const url = root(`social/cb/${cb}&check_accept=1&is_accept=${is_accept}`)

      const hash = isStage ? '' : await collectAndHash()
      const headers = generateRequestHashHeader(hash)

      return client.get(url, { headers })
    },
  }
}
